import React, { useEffect, useMemo, useState } from 'react';
import {
  UpcomingAppointments,
  useAppointmentTimer,
  useGetUpcomingAppointment,
} from 'hooks/useGetUpcomingAppointment';
import { useIsJoinable } from 'hooks/useIsJoinable';
import ButtonComponent from '../button/buttonComponent';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds } from 'utilities/constants';
import { twMerge } from 'tailwind-merge';
import { useAppointmentNotificationColor } from '../../hooks/useAppointmentNotificationColor';
import { useFirstJoinableIndex } from '../../hooks/useFirstJoinableIndex';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { interpolateReactNodes } from '../../utilities/functions';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { DISMISSED_JOIN_NOW_APPOINTMENT_IDS } from '../../utilities/constants/appointment';
import { useIsMobile } from '../../hooks/useIsMobile';

const classes = new Map([
  [true, ''],
  [false, 'text-bright-green border-bright-green bg-green-50'],
]);

const getClasses = (isNotStarted: boolean, timeColor: string | null) => {
  return twMerge(
    'rounded-full font-semibold py-0.5 px-3 desktop:ml-2.5 border mr-4',
    !isNotStarted
      ? classes.get(isNotStarted)
      : `text-[${timeColor}] border-[${timeColor}] bg-transparent`,
  );
};

interface JoinToAppointment {
  appointment: UpcomingAppointments;
  handleNameClick: (appointment: UpcomingAppointments) => void;
  handleJoinAppointment: (appointment: UpcomingAppointments) => void;
  isFirstJoinable?: boolean;
}

const JoinToAppointment: React.FC<JoinToAppointment> = ({
  appointment,
  handleJoinAppointment,
  handleNameClick,
  isFirstJoinable,
}) => {
  const { data: locale } = useGetComponent({
    componentId: componentIds.UPCOMING_APPOINTMENTS_PROVIDER,
    locale: 'en',
  });

  const isMobile = useIsMobile();

  const { setItemWithUniqueKey, getItemWithUniqueKey } = useLocalStorage();

  const [dismissedJoinAppointments, setDismissedJoinAppointments] = useState<
    string[]
  >([]);

  const { timer, isNotStarted } = useAppointmentTimer(
    appointment?.date,
    appointment?.end,
  );

  const { timeColor, baseColor } = useAppointmentNotificationColor({
    startDate: appointment?.date as string,
  });

  const timeClassName = useMemo(
    () => getClasses(isNotStarted, timeColor),
    [isNotStarted, timeColor],
  );

  const { isJoinable } = useIsJoinable({
    appointmentStatus: appointment?.appointmentStatus,
    endDate: appointment?.end,
    startDate: appointment?.date as string,
  });

  const handleClickJoin = () => {
    setDismissedJoinAppointments((prev) => {
      const newValue = [...prev, appointment.appointmentCodexId];

      setItemWithUniqueKey(
        DISMISSED_JOIN_NOW_APPOINTMENT_IDS,
        JSON.stringify(newValue),
      );

      return newValue;
    });

    handleJoinAppointment(appointment);
  };

  const buttonShouldPulse =
    isNotStarted &&
    !dismissedJoinAppointments.includes(appointment.appointmentCodexId);

  useEffect(() => {
    const dismissedJoinNowAppointments = JSON.parse(
      getItemWithUniqueKey(DISMISSED_JOIN_NOW_APPOINTMENT_IDS),
    );

    setDismissedJoinAppointments(dismissedJoinNowAppointments || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isJoinable || !appointment) {
    return null;
  }

  return (
    <div
      style={{
        borderBottomColor: baseColor || 'transparent',
      }}
      className={twMerge(
        'grid grid-cols-12 gap-2.5 w-full bg-white py-4 border border-t-gray-opacity-15 px-7 desktop:px-[135px]',
        isFirstJoinable && baseColor ? 'border-b-[5px]' : 'border-b-0',
      )}
    >
      <div className="flex flex-col desktop:flex-row items-center desktop:col-span-10 col-span-full">
        {isNotStarted && (
          <div className={`text-[${baseColor}] hidden desktop:inline-block`}>
            <CalendarIcon className={'fill-current w-6 h-6 mr-1'} />
          </div>
        )}
        <div className="flex items-center max-desktop:w-full">
          <span className="desktop:text-title desktop:whitespace-nowrap text-base text-neutral-800 font-semibold">
            {interpolateReactNodes(
              isNotStarted
                ? locale?.currentAppointmentWith ||
                    'Stay on schedule! Your session with {{name}} is active'
                : locale?.currentActiveAppointmentWith ||
                    'Stay on schedule! Your session with {{name}} is active',
              {
                name: (
                  <ButtonComponent
                    type="underline"
                    onClick={() => handleNameClick(appointment)}
                    noUpperCase
                    testID={'navigate'}
                    className="ml-1 !w-auto !text-clc-blue desktop:!text-h5 text-base whitespace-nowrap"
                  >
                    {appointment?.name}
                  </ButtonComponent>
                ),
              },
            )}
          </span>
        </div>

        {!isNotStarted && (
          <span className="hidden desktop:inline-block mx-2 text-med-gray-3">
            •
          </span>
        )}

        <div className="flex w-full items-center max-desktop:mt-2.5">
          {(!isNotStarted || isMobile) && (
            <span className="text-title text-neutral-800 font-semibold mr-1">
              {locale?.time}
            </span>
          )}
          <span className={timeClassName}>{timer}</span>
        </div>
      </div>
      <div className="desktop:col-span-2 col-span-full">
        <ButtonComponent
          testID={'join-now'}
          className={`!text-xs !px-9 py-2 !w-full ${
            buttonShouldPulse ? 'button-pulse-blue' : ''
          }`}
          desktopFullWidth
          onClick={handleClickJoin}
        >
          {locale?.joinNow}
        </ButtonComponent>
      </div>
    </div>
  );
};

const JoinToAppointmentOutlet = () => {
  const {
    appointments = [],
    handleNameClick,
    handleJoinAppointment,
  } = useGetUpcomingAppointment();

  const firstJoinableIndex = useFirstJoinableIndex(appointments);

  return (
    <>
      {appointments.map((appointment, index) => (
        <JoinToAppointment
          key={appointment.appointmentCodexId}
          appointment={appointment}
          handleNameClick={handleNameClick}
          handleJoinAppointment={handleJoinAppointment}
          isFirstJoinable={firstJoinableIndex === index}
        />
      ))}
    </>
  );
};

export default JoinToAppointmentOutlet;
